import React, { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  Box, 
  CircularProgress,
  Select,
  MenuItem,
  Stack,
  Divider
} from '@mui/material';
import { Delete, Download } from '@mui/icons-material';
import { serviceModulesApi } from '../../../api/serviceModulesApi';
import { LatestPersonalDocumentResponse } from '../../../types/personalDocuments';
import { downloadFileFromUrl } from '../../../common/logic/FileDownloader';
import { useTranslation } from 'react-i18next';
import { GeneralButton } from '../../../common/ui/GeneralButton';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { handleRequest } from '../../../store/utils/handleRequest';
import ConfirmDeleteDialog from '../../../components/common/table/ConfirmDeleteDialog';
import dayjs from 'dayjs';

interface Props {
  open: boolean;
  onClose: () => void;
  serviceModuleId: number;
}

const countRange = [5, 20, 50, 75, 100];

export const PersonalDocumentsDialogLatest: React.FC<Props> = ({ open, onClose, serviceModuleId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [count, setCount] = useState(countRange[0]);
  const [documents, setDocuments] = useState<LatestPersonalDocumentResponse[] | undefined>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [deleteDoc, setDeleteDoc] = useState<LatestPersonalDocumentResponse | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const loadDocuments = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    const response = await serviceModulesApi.getLatestPersonalDocuments(serviceModuleId, count);
    if (response.success() && response.result) {
      setDocuments(response.result);
    }
    else {
      setDocuments(undefined);
    }
    if (showLoader) setIsLoading(false);
  }, [count, serviceModuleId]);

  useEffect(() => {
    if (open) {
      loadDocuments();
    } else {
      setDocuments([]);
    }
  }, [open, loadDocuments]);

  const handleDownload = (doc: LatestPersonalDocumentResponse) => {
    if (doc.blobPath && doc.filename) {
      downloadFileFromUrl(doc.blobPath, doc.filename);
    }
  };

  const handleDeleteClick = (doc: LatestPersonalDocumentResponse) => {
    setDeleteDoc(doc);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteDoc) {
      const response = await handleRequest(
        dispatch,
        "direct",
        () => serviceModulesApi.deletePersonalDocumentFromEmployee(serviceModuleId, deleteDoc.employeeId, deleteDoc.id),
        t("requestMessage.personalDocumentDeleted")
      );
      if (response.success()) {
        loadDocuments(false);
      }
    }
    setDeleteDoc(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogLatest.title")}
        </DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 1,
            py: 1,
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 ,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper', 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogLatest.count")}:
            </Typography>
            <Select
              size="small"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              sx={{ minWidth: 80 }}
            >
              {countRange.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </Box>
          <PersonalDocumentsListLatestDocuments
            isLoading={isLoading}
            documents={documents}
            onDownloadClick={handleDownload}
            onDeleteClick={handleDeleteClick}
          />
        </DialogContent>
        <DialogActions>
          <GeneralButton onClick={onClose} appVariant="secondary">
            {t("buttons.close")}
          </GeneralButton>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={isConfirmDialogOpen}
        dialogMessage={t("serviceModulesPage.personalDocumentsPage.shared.confirmDelete")}
        handleDialogClose={() => setIsConfirmDialogOpen(false)}
        handleOnDelete={handleConfirmDelete}
      />
    </>
  );
};

interface PersonalDocumentsListLatestDocumentsProps {
  isLoading: boolean;
  documents: LatestPersonalDocumentResponse[] | undefined;
  onDownloadClick: (document: LatestPersonalDocumentResponse) => void;
  onDeleteClick: (document: LatestPersonalDocumentResponse) => void;
}

const PersonalDocumentsListLatestDocuments: React.FC<PersonalDocumentsListLatestDocumentsProps> = ({ isLoading, documents, onDownloadClick, onDeleteClick }) => {
  const { t } = useTranslation();

  // Show loading if no data loaded yet
  if (isLoading && (!documents || documents.length === 0)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (documents === undefined) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        {t("serviceModulesPage.personalDocumentsPage.shared.failedLoading")}
      </Typography>
    );
  }

  if (documents.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        {t("serviceModulesPage.personalDocumentsPage.shared.noDocumentsAvailable")}:
      </Typography>
    );
  }

  return (
    <List>
      {documents.map((doc, index) => (
        <React.Fragment key={doc.id}>
          <ListItem
            key={doc.id}
            secondaryAction={
              <Stack direction="row" spacing={1}>
                <IconButton edge="end" onClick={() => onDownloadClick(doc)} title={t("buttons.download")}>
                  <Download />
                </IconButton>
                <IconButton edge="end" onClick={() => onDeleteClick(doc)} title={t("buttons.delete")} color="error">
                  <Delete />
                </IconButton>
              </Stack>
            }
          >
            <ListItemText
              primary={doc.filename}
              secondary={
                <>
                  <Typography variant="caption" component="span" sx={{ display: "block" }} color="textSecondary">
                    {t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogLatest.employee")}: {doc.firstName} {doc.lastName}
                  </Typography>
                  <Typography variant="caption" component="span" sx={{ display: "block" }} color="textSecondary">
                    {t("serviceModulesPage.personalDocumentsPage.shared.uploadedAt")}: {dayjs(doc.uploadDatetime).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                </>
              }
            />
          </ListItem>
          {index < documents.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
}
